import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Sidebar } from "@/components/app/Sidebar";
import { TopBar } from "@/components/app/TopBar";
import { Breadcrumbs } from "@/components/app/Breadcrumbs";
import { CommandPalette } from "@/components/app/CommandPalette";
import { Footer } from "@/components/app/Footer";
import { ImpersonationBanner } from "@/components/app/ImpersonationBanner";
import { VerifyEmailBanner } from "@/components/app/VerifyEmailBanner";
import { BreadcrumbProvider } from "@/lib/breadcrumbCache";
import { ProgrammeProvider } from "@/lib/useProgramme";
import { useSessionExpiry } from "@/lib/useSessionExpiry";
import { useAuthBootstrap } from "@/lib/useAuthBootstrap";

export function AppLayout() {
  const location = useLocation();
  const phase = useAuthBootstrap();

  if (phase === "unauthed") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search } }}
      />
    );
  }

  if (phase === "bootstrapping") {
    return <AuthBootstrapSkeleton />;
  }

  return (
    <ProgrammeProvider>
      <BreadcrumbProvider>
        <SessionExpiryMount />
        <AppShell />
      </BreadcrumbProvider>
    </ProgrammeProvider>
  );
}

// Shown for the (usually sub-second) window while the bootstrap
// hook refreshes a stale access token. Mimics the real shell so
// the eye doesn't jump — Sidebar silhouette on the left, a
// centred loader on the right. No API calls here: the goal is to
// avoid firing anything authenticated until the token is fresh.
function AuthBootstrapSkeleton() {
  return (
    <div className="relative flex min-h-screen bg-background">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-brand-glow opacity-60"
      />
      <aside
        aria-hidden
        className="hidden w-64 shrink-0 border-r border-border/60 bg-background/70 backdrop-blur-sm lg:block"
      />
      <div
        role="status"
        aria-live="polite"
        className="flex min-w-0 flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
      >
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Checking your session…</p>
      </div>
    </div>
  );
}

function SessionExpiryMount() {
  useSessionExpiry();
  return null;
}

// Visually-hidden polite live region that announces the current page on
// navigation only. `usePageTitle` writes the per-route title to
// document.title as "<page> · ExamBase"; we read it back one tick after
// the route changes (so the page's own usePageTitle effect has run) and
// surface just the "<page>" portion. Screen readers announce this single
// short string per navigation instead of re-reading the whole <Outlet>.
function RouteAnnouncer() {
  const location = useLocation();
  const [label, setLabel] = useState("");

  useEffect(() => {
    // Defer to the end of the microtask/paint so the destination page's
    // usePageTitle effect (which runs after this one on a route change)
    // has already updated document.title.
    const id = window.setTimeout(() => {
      const title = document.title.split(" · ")[0]?.trim();
      setLabel(title ? `${title}, page loaded` : "Page loaded");
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {label}
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  return (
    <div className="relative flex min-h-screen bg-background">
      {/* Subtle ambient gradient behind the whole shell. Fixed so it doesn't
          scroll; low enough opacity that long pages still feel quiet. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-brand-glow opacity-60"
      />
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <VerifyEmailBanner />
        <TopBar onSignedInOut />
        <main
          id="main"
          tabIndex={-1}
          role="main"
          className={`mx-auto flex w-full flex-1 flex-col gap-6 px-4 py-6 md:px-8 ${
            // Data-dense pages (the question bank list + review/moderation
            // queues) use the full viewport width; everything else stays
            // in the readable 7xl column.
            location.pathname === "/platform/questions" ||
            location.pathname === "/platform/questions/review-queue" ||
            location.pathname === "/platform/questions/moderation"
              ? "max-w-none"
              : "max-w-7xl"
          }`}
        >
          <Breadcrumbs />
          {/* Page-wide aria-live used to wrap the whole <Outlet>, which made
              screen readers re-announce the entire page on any DOM change
              (a live timer, a toast, a fetched list). Scope it instead to a
              visually-hidden region that announces ONLY the current page
              title on navigation. */}
          <RouteAnnouncer />
          <div className="flex-1">
            <Outlet />
          </div>
        </main>
        <Footer variant="app" />
      </div>
      <CommandPalette />
    </div>
  );
}
